import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import {Button, Icon, Popup, Select, Text, Label} from '@gravity-ui/uikit';
import {Plus, ArrowRotateRight} from '@gravity-ui/icons';
import {SelectFunds, SelectRegions, useCategoryGroupsWithCategories} from '../../../../entities/';
import styles from './Filters.module.scss';
import {useSearchParams} from 'react-router';

const STATUS_OPTIONS = [
  {value: 'Created', content: 'Создан'},
  {value: 'Published', content: 'Опубликован'},
  {value: 'AmountCollected', content: 'Средства собраны'},
  {value: 'Completed', content: 'Завершен'},
  {value: 'Cancelled', content: 'Отменен'},
];

type StaticFilterType = 'regions' | 'funds' | 'statuses';
type GroupFilterType = `group:${string}`;
type FilterType = StaticFilterType | GroupFilterType;

interface FilterConfig {
  type: FilterType;
  buttonText: string;
  isGroup: boolean;
  groupId?: string;
}

const STATIC_FILTER_CONFIGS: FilterConfig[] = [
  {type: 'funds', buttonText: 'Фонд', isGroup: false},
  {type: 'statuses', buttonText: 'Статус', isGroup: false},
  {type: 'regions', buttonText: 'Регион', isGroup: false},
];

const isGroupType = (type: FilterType): type is GroupFilterType => type.startsWith('group:');

export const Filters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);

  const {data: categoryGroups} = useCategoryGroupsWithCategories();

  const groupsByCategoryId = useMemo(() => {
    const map: Record<string, string> = {};
    (categoryGroups || []).forEach((group) => {
      group.categories.forEach((category) => {
        map[category.id] = group.id;
      });
    });
    return map;
  }, [categoryGroups]);

  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (categoryGroups || []).forEach((group) => {
      group.categories.forEach((category) => {
        map[category.id] = category.name;
      });
    });
    return map;
  }, [categoryGroups]);

  const filterConfigs = useMemo<FilterConfig[]>(() => {
    const groupConfigs: FilterConfig[] = (categoryGroups || []).map((group) => ({
      type: `group:${group.id}` as GroupFilterType,
      buttonText: group.name,
      isGroup: true,
      groupId: group.id,
    }));
    return [...groupConfigs, ...STATIC_FILTER_CONFIGS];
  }, [categoryGroups]);

  const [tempFilters, setTempFilters] = useState<Record<string, string[]>>({});

  const buildStateFromParams = useCallback((): Record<string, string[]> => {
    const next: Record<string, string[]> = {};
    STATIC_FILTER_CONFIGS.forEach((config) => {
      const value = searchParams.get(config.type);
      next[config.type] = value ? value.split(',').filter(Boolean) : [];
    });
    (categoryGroups || []).forEach((group) => {
      next[`group:${group.id}`] = [];
    });
    const categoriesParam = searchParams.get('categories');
    if (categoriesParam) {
      const ids = categoriesParam.split(',').filter(Boolean);
      ids.forEach((id) => {
        const groupId = groupsByCategoryId[id];
        if (groupId) {
          const key = `group:${groupId}`;
          next[key] = [...(next[key] || []), id];
        }
      });
    }
    return next;
  }, [searchParams, categoryGroups, groupsByCategoryId]);

  useEffect(() => {
    setTempFilters(buildStateFromParams());
  }, [buildStateFromParams]);

  const appliedFilters = useMemo(() => buildStateFromParams(), [buildStateFromParams]);

  const getFilterItems = useCallback(
    (type: FilterType, ids: string[]): {id: string; name: string}[] => {
      if (ids.length === 0) {
        return [];
      }

      if (type === 'statuses') {
        return ids
          .map((id) => {
            const option = STATUS_OPTIONS.find((opt) => opt.value === id);
            return option ? {id, name: option.content} : null;
          })
          .filter((value): value is {id: string; name: string} => Boolean(value));
      }

      if (isGroupType(type)) {
        return ids
          .map((id) => {
            const name = categoryNameById[id];
            return name ? {id, name} : null;
          })
          .filter((value): value is {id: string; name: string} => Boolean(value));
      }

      try {
        const stored = sessionStorage.getItem(`${type}-filter`);
        if (!stored) return [];
        const parsed = JSON.parse(stored) as {id: string; name: string}[];
        return ids
          .map((id) => {
            const item = parsed.find((entry) => entry.id === id);
            return item ? {id, name: item.name} : null;
          })
          .filter((value): value is {id: string; name: string} => Boolean(value));
      } catch (error) {
        console.error(`Ошибка при получении данных из sessionStorage для ${type}:`, error);
        return [];
      }
    },
    [categoryNameById],
  );

  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleFilterToggle = useCallback((filterType: FilterType) => {
    setActiveFilter((prev) => (prev === filterType ? null : filterType));
  }, []);

  const handleFilterUpdate = useCallback((filterType: FilterType, values: string[]) => {
    setTempFilters((prev) => ({
      ...prev,
      [filterType]: values,
    }));
  }, []);

  const writeStateToParams = useCallback(
    (state: Record<string, string[]>) => {
      const newParams = new URLSearchParams(searchParams);

      STATIC_FILTER_CONFIGS.forEach((config) => {
        const values = state[config.type] || [];
        if (values.length > 0) {
          newParams.set(config.type, values.join(','));
        } else {
          newParams.delete(config.type);
        }
      });

      const allCategories = (categoryGroups || []).flatMap(
        (group) => state[`group:${group.id}`] || [],
      );
      if (allCategories.length > 0) {
        newParams.set('categories', allCategories.join(','));
      } else {
        newParams.delete('categories');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, categoryGroups],
  );

  const handleApplyFilters = useCallback(() => {
    writeStateToParams(tempFilters);
    setActiveFilter(null);
  }, [writeStateToParams, tempFilters]);

  const handleResetFilterType = useCallback(
    (type: FilterType) => {
      const next = {...tempFilters, [type]: []};
      setTempFilters(next);
      writeStateToParams(next);

      if (!isGroupType(type) && type !== 'statuses') {
        try {
          sessionStorage.removeItem(`${type}-filter`);
        } catch (error) {
          console.error(`Ошибка при очистке sessionStorage для ${type}:`, error);
        }
      }
    },
    [tempFilters, writeStateToParams],
  );

  const handleRemoveFilter = useCallback(
    (type: FilterType, filterId: string) => {
      const currentValues = appliedFilters[type] || [];
      const newValues = currentValues.filter((id) => id !== filterId);

      const next = {...tempFilters, [type]: newValues};
      setTempFilters(next);
      writeStateToParams(next);

      if (!isGroupType(type) && type !== 'statuses') {
        try {
          const stored = sessionStorage.getItem(`${type}-filter`);
          if (stored) {
            const parsed = JSON.parse(stored) as {id: string; name: string}[];
            const updated = parsed.filter((item) => item.id !== filterId);
            sessionStorage.setItem(`${type}-filter`, JSON.stringify(updated));
          }
        } catch (error) {
          console.error(`Ошибка при обновлении sessionStorage для ${type}:`, error);
        }
      }
    },
    [appliedFilters, tempFilters, writeStateToParams],
  );

  const renderFilterContent = useCallback(
    (config: FilterConfig) => {
      const currentValues = tempFilters[config.type] || [];

      if (config.isGroup && config.groupId) {
        const group = (categoryGroups || []).find((g) => g.id === config.groupId);
        const options =
          group?.categories.map((category) => ({
            value: category.id,
            content: category.name,
          })) || [];
        return (
          <div className={styles.filterContent}>
            <Select
              size="m"
              options={options}
              value={currentValues}
              onUpdate={(values) => handleFilterUpdate(config.type, values)}
              placeholder="Выберите категории"
              multiple
              filterable
              filterPlaceholder="Поиск"
              disabled={options.length === 0}
            />
            <Button view="action" size="m" onClick={handleApplyFilters} className={styles.applyButton}>
              Применить
            </Button>
          </div>
        );
      }

      switch (config.type) {
        case 'regions':
          return (
            <div className={styles.filterContent}>
              <SelectRegions
                isSaveSessionStorage
                name="filter"
                value={currentValues}
                onUpdate={(values) => handleFilterUpdate('regions', values)}
                size="m"
                placeholder="Выберите регион"
                multiple
              />
              <Button view="action" size="m" onClick={handleApplyFilters} className={styles.applyButton}>
                Применить
              </Button>
            </div>
          );
        case 'funds':
          return (
            <div className={styles.filterContent}>
              <SelectFunds
                isSaveSessionStorage
                name="filter"
                value={currentValues}
                onUpdate={(values) => handleFilterUpdate('funds', values)}
                size="m"
                placeholder="Выберите фонды"
                multiple
              />
              <Button view="action" size="m" onClick={handleApplyFilters} className={styles.applyButton}>
                Применить
              </Button>
            </div>
          );
        case 'statuses':
          return (
            <div className={styles.filterContent}>
              <Select
                options={STATUS_OPTIONS}
                value={currentValues}
                onUpdate={(values) => handleFilterUpdate('statuses', values)}
                size="m"
                placeholder="Выберите статусы"
                multiple
                filterable
                filterPlaceholder="Поиск"
              />
              <Button view="action" size="m" onClick={handleApplyFilters} className={styles.applyButton}>
                Применить
              </Button>
            </div>
          );
        default:
          return null;
      }
    },
    [tempFilters, handleFilterUpdate, handleApplyFilters, categoryGroups],
  );

  const renderAppliedFilters = useCallback(
    (type: FilterType) => {
      const ids = appliedFilters[type] || [];
      if (ids.length === 0) {
        return null;
      }
      const items = getFilterItems(type, ids);
      return (
        <div className={styles.appliedFilters}>
          {items.map((item) => (
            <div key={item.id} className={styles.filterLabelWrapper}>
              <Label
                type="close"
                className={styles.filterLabel}
                size="xs"
                theme="normal"
                onCloseClick={() => handleRemoveFilter(type, item.id)}>
                {item.name}
              </Label>
            </div>
          ))}
        </div>
      );
    },
    [appliedFilters, getFilterItems, handleRemoveFilter],
  );

  return (
    <div className={styles.filtersContainer}>
      <div className={styles.filters}>
        {filterConfigs.map((config) => {
          const isActive = activeFilter === config.type;
          const hasFilters = (appliedFilters[config.type] || []).length > 0;

          return (
            <div key={config.type} className={styles.filterContainer}>
              <Text variant="subheader-1">{config.buttonText}</Text>
              {renderAppliedFilters(config.type)}
              <Button
                ref={(el) => {
                  buttonRefs.current[config.type] = el;
                }}
                view="normal"
                size="xs"
                onClick={() => handleFilterToggle(config.type)}
                className={styles.filterButton}>
                <Icon data={Plus} width={12} height={12} />
              </Button>
              {hasFilters && (
                <Button
                  size="xs"
                  view="flat"
                  onClick={() => handleResetFilterType(config.type)}
                  className={styles.resetTypeButton}>
                  <Icon data={ArrowRotateRight} width={12} height={12} />
                  Сбросить
                </Button>
              )}
              <Popup
                open={isActive}
                onOpenChange={() => setActiveFilter(null)}
                anchorElement={buttonRefs.current[config.type] ?? undefined}
                placement="bottom-start">
                <div className={styles.popupContent}>{renderFilterContent(config)}</div>
              </Popup>
            </div>
          );
        })}
      </div>
    </div>
  );
};
